import useStyles from 'isomorphic-style-loader/useStyles';
import { EyeOff } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Loader } from 'semantic-ui-react';

import { extractSearchPages } from '@store/search';
import { AnchoreLink } from '@components/anchore-link';
import Err from '@components/err';
import { InfiniteScroll } from '@components/infinite-scroll';

import { useListVulnerabilityPackagesInfiniteQuery } from '../../applications.api';

import { PackageSection } from './affected-packages-panel-section';
import { DEFAULT_PANEL_ENTITY } from './affected-packages-panel.const';
import s from './affected-packages-panel.scss';

export interface AffectedPackagesPanelProps {
  appId: string;
  versionId: string;
  vulnerabilityId: string;
  /**
   * Noun for the row the panel is scoped to, used in the filtered-out notice.
   *
   * @default 'vulnerability'
   */
  entity?: string;
  /**
   * Whether the row is excluded by the table's active filters.
   */
  filteredOut?: boolean;
}

export const AffectedPackagesPanel = ({
  appId,
  versionId,
  vulnerabilityId,
  entity = DEFAULT_PANEL_ENTITY,
  filteredOut,
}: AffectedPackagesPanelProps) => {
  useStyles(s);

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useListVulnerabilityPackagesInfiniteQuery({
    appId,
    versionId,
    vulnerabilityId,
  });

  const packages = extractSearchPages(data);

  // The first package is expanded by default; other packages are collapsed.
  // We track which packages the user has flipped from their default rather
  // than absolute expansion state — that way the first package renders
  // expanded on its very first paint (no post-mount state update), so the
  // accordion entry animation doesn't play when the side panel opens.
  const [userToggled, setUserToggled] = useState<Set<string>>(new Set());

  const togglePackage = useCallback((id: string) => {
    setUserToggled(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const isExpanded = (id: string, isDefault: boolean) =>
    userToggled.has(id) ? !isDefault : isDefault;

  return (
    <div className={s.root}>
      {filteredOut && (
        <div className={s.filteredOutNotice} role="status">
          <EyeOff className={s.filteredOutNoticeIcon} aria-hidden="true" />
          <span>
            This {entity} is hidden by the table&rsquo;s active filters.
          </span>
        </div>
      )}

      <div className={s.toolbar}>
        <span className={s.toolbarLabel}>View vulnerability details at</span>
        <AnchoreLink vulnerabilityId={vulnerabilityId} />
      </div>

      <div className={s.body}>
        {error ? (
          <Err error={error} />
        ) : isLoading ? (
          <div className={s.loader}>
            <Loader active inline="centered" size="small" />
          </div>
        ) : packages.length === 0 ? (
          <div className={s.emptyMessage}>No affected packages found</div>
        ) : (
          <InfiniteScroll
            hasMore={hasNextPage}
            isLoading={isFetchingNextPage || (isFetching && !isLoading)}
            loadMore={() => {
              fetchNextPage();
            }}
            hasScrollableParent
          >
            {packages.map((pkg, idx) => {
              // Identity for expand/collapse state. `purl` is the canonical
              // platform identifier; fall back to `namespace:name` for
              // non-purl entries (e.g. some OS package types).
              const id = pkg.purl ?? `${pkg.namespace}:${pkg.name}`;
              return (
                <PackageSection
                  key={id}
                  appId={appId}
                  expanded={isExpanded(id, idx === 0)}
                  onToggle={() => togglePackage(id)}
                  pkg={pkg}
                  versionId={versionId}
                  vulnerabilityId={vulnerabilityId}
                />
              );
            })}
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
};
